import { Component, EventEmitter, Input, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppComponent } from '../../app.component';
import { AddressModel } from '../../model/address.model';
import { CountryModel } from '../../model/country.model';
import { IndividualAddressModel } from '../../model/individual-address.model';
import { WizardService } from '../../wizard/wizard.service';
import { AddressService } from '../address.service';
import { AddressFormatModel } from './address-format.model';

const jQuery = window['jQuery'] || {};

@Component({
  selector: 'app-dynamic-address',
  templateUrl: './dynamic-address.component.html',
  styleUrls: ['./dynamic-address.component.css']
})
export class DynamicAddressComponent implements OnInit {

  @Input() fieldset: HTMLFieldSetElement;
  @Input() group: UntypedFormGroup;
  @Input() Country: CountryModel;
  @Input() CountryChange: EventEmitter<CountryModel>;
  @Input() Address: AddressModel;
  @Input() AddressFields: 'FULL' | 'CITY_STATE' = 'FULL';

  public get isSaving(): boolean {
    return this.service.isSaving;
  }

  public set isSaving(state: boolean) {
    this.service.isSaving = state;
    this.app.wait(state);
  }

  public dynamic: AddressFormatModel;

  private _country: CountryModel = null;

  public adr_line1HasError: Observable<boolean>;
  public adr_cityHasError: Observable<boolean>;
  public adr_stateHasError: Observable<boolean>;
  public adr_intl_provinceHasError: Observable<boolean>;
  public adr_post_codeHasError: Observable<boolean>;

  constructor(
    private service: AddressService,
    private wizard: WizardService,
    private app: AppComponent
  ) {
    this.isSaving = false;
  }

  public ngOnInit() {
    this.Address = this.Address || new AddressModel();

    if (this.AddressFields == 'FULL') {
      const adr_line1 = new UntypedFormControl(this.Address.adr_line1, [Validators.required]);
      const adr_line2 = new UntypedFormControl(this.Address.adr_line2);

      this.group.registerControl('adr_line1', adr_line1);
      this.group.registerControl('adr_line2', adr_line2);
    }
    const adr_city = new UntypedFormControl(this.Address.adr_city, [Validators.required]);
    const adr_state = new UntypedFormControl(this.Address.adr_state, [Validators.required]);
    const adr_intl_province = new UntypedFormControl(this.Address.adr_intl_province, [Validators.required]);
    const adr_post_code = new UntypedFormControl(this.Address.adr_post_code, [Validators.required]);

    this.group.registerControl('adr_city', adr_city);
    this.group.registerControl('adr_state', adr_state);
    this.group.registerControl('adr_intl_province', adr_intl_province);
    this.group.registerControl('adr_post_code', adr_post_code);

    if (this.wizard.Address instanceof Observable) {
      this.wizard.Address.subscribe(address => {
        this.UpdateForm(address);
      });
    } else if (this.Address.adr_country) {
      this.UpdateForm(this.Address);
    }

    this.CountryChange.subscribe(country => {
      this.OnCountryChange(country);

      if (this.fieldset) {
        jQuery(':input', this.fieldset).first().focus();
      }
    });

    for (const key of ['adr_line1', 'adr_city', 'adr_state', 'adr_intl_province', 'adr_post_code']) {
      const control = this.group.get(key);
      if (control) {
        this[key + 'HasError'] = control.statusChanges.pipe(
          map(status => status == 'INVALID' && (control.dirty || control.touched))
        );
      }
    }
  }

  private UpdateForm(address: IndividualAddressModel | AddressModel) {
    const country = this.service.GetCountry(address.adr_country);
    if (country) {
      this.group.reset(address);
      this.group.get('adr_country').reset(country);

      this.service.GetAddressFormat(country).subscribe(result => {
        this.isSaving = false;
        this.dynamic = result;

        // Reset required fields
        const controlMap = {
          'locality': 'adr_city',
          'administrative_area': (): string => {
            return result.administrative_areas ? 'adr_state' : 'adr_intl_province';
          },
          'postal_code': 'adr_post_code'
        };

        if (result.administrative_areas) {
          this.group.get('adr_intl_province').setValidators([]);
        } else {
          this.group.get('adr_state').setValidators([]);
        }

        Object.entries(controlMap).forEach(([key, value]) => {
          const name = typeof value === 'function' ? value() : value.toString();

          if (result.format.required_fields.indexOf(key) > -1) {
            this.group.get(name).setValidators([Validators.required]);
          } else {
            this.group.get(name).setValidators([]);
          }
        });

        if (address.adr_state) {
          const state = this.service.GetAdministrativeArea(address.adr_country, address.adr_state);
          if (state) {
            this.group.get('adr_state').reset(state);
          }
        }
      });
    }
  }

  private OnCountryChange(country: CountryModel) {
    if (country === null) {
      this.isSaving = false;
      this.dynamic = null;
      return;
    }

    this.isSaving = true;

    this.service.GetAddressFormat(country.cty_code).subscribe(result => {
      this.isSaving = false;
      this.dynamic = result;

      // Reset required fields
      const controlMap = {
        'locality': 'adr_city',
        'administrative_area': (): string => {
          return result.administrative_areas ? 'adr_state' : 'adr_intl_province';
        },
        'postal_code': 'adr_post_code'
      };

      if (result.administrative_areas) {
        this.group.get('adr_intl_province').setValidators([]);
      } else {
        this.group.get('adr_state').setValidators([]);
      }

      Object.entries(controlMap).forEach(([key, value]) => {
        const name = typeof value === 'function' ? value() : value.toString();

        if (result.format.required_fields.indexOf(key) > -1) {
          this.group.get(name).setValidators([Validators.required]);
        } else {
          this.group.get(name).setValidators([]);
        }
      });

      // Reset state field as weird things happen when it toggles between a select and input
      this.group.get('adr_state').reset(null);
      this.group.get('adr_intl_province').reset(null);
    });
  }

}
